/**
 * AihubAuthModal
 *
 * Generic authentication modal for AIHub loadboards (Echo, SPOT, BEON, Navisphere, Arrive,
 * TQL, Emerge, RXO, Mode Global, KNX, Schneider, DAT, TruckStop Account, JB Hunt).
 *
 * Flow:
 *   1. User clicks "Log in to {Loadboard}" → we open the loadboard URL in a new tab.
 *   2. We poll the backend every 1.5s for `isConnected` and monitor the opened tab.
 *      The extension's content script reads the loadboard's localStorage/cookies, sends
 *      the token to the extension background → loads-ingest-service → Firestore; our poll
 *      sees `isConnected: true` and transitions to the `success` step.
 *   3. While the opened tab is still open, we wait indefinitely (the user is logging in).
 *      If the tab is closed without a successful connection, we give a short grace period
 *      (10s) then show an `error` step with a Retry button.
 *   4. A hard safety cap of 3 minutes prevents truly stuck sessions.
 *
 * Tab-aware approach (was fixed 20s countdown):
 *   A fixed 20s countdown was too short for users who need to actually log in (type
 *   credentials, complete 2FA, etc.). Now we keep waiting as long as the loadboard tab
 *   is open — the user clearly intends to finish. Once the tab closes we give 10s grace
 *   for the token handshake to land, then surface the error with Retry.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, DialogTitle, DialogContent, CircularProgress } from '@mui/material'
import { IconButton } from '@mui/joy'
import { Close, OpenInNew, CheckCircle, Extension as ExtensionIcon, ErrorOutline, Refresh, Lock, ArrowBack } from '@mui/icons-material'
import { useAppSelector } from '@/hooks'
import { selectFeatureFlags } from '@/store/authSlice'
import { formatPhone } from '@/utils/formatPhone'
import { AihubBoardStatus, AihubSource } from '../hooks/useAihubTokenStatus'
import { PasswordInput } from './PasswordInput'
import type { ArriveOtpMethod } from '../api/loadboards.api'
import { beginConnectionAttempt, trackConnectionDialogOpened, trackConnectionMethodSelected, type ConnectionAttempt } from '../utils/sourceAnalytics'
import { completeSuccessfulConnectionAttempt } from '../utils/connectionAttempt'
import { HAS_EXTENSION_KEY } from '../utils/constants'
import {
    arriveLoginCredentials,
    arriveVerifyOtp,
    beonLoginCredentials,
    beonVerifyOtp,
    convoyLoginCredentials,
    convoyVerifyOtp,
    echoLoginCredentials,
    echoVerifyOtp,
    emergeLoginCredentials,
    knxLoginCredentials,
    modeGlobalLoginCredentials,
    navisphereLoginCredentials,
    navisphereVerifyOtp,
    rxoLoginCredentials,
    rxoVerifyOtp,
    schneiderLoginCredentials,
    schneiderVerifyOtp,
    truckstopAccountLoginCredentials,
    truckstopAccountVerifyOtp,
    spotLoginCredentials,
    spotVerifyOtp,
    tqlLoginCredentials,
    tqlVerifyOtp,
} from '../api/loadboards.api'

interface AihubAuthModalProps {
    open: boolean
    loadboard: AihubSource | null
    onClose: () => void
    onSuccess: () => void
    checkStatus: (source: AihubSource) => Promise<AihubBoardStatus | null>
    onStatusRefreshed?: (source: AihubSource, status: AihubBoardStatus | null) => void
    initialStatus?: AihubBoardStatus | null
    analyticsSurface?: 'load_hub' | 'ai_hub' | 'load_radar'
    /**
     * Offer ONLY the extension capture, whatever the credential flag says.
     *
     * For a surface that deliberately carries one way to connect — the Box Truck board, where the operator
     * logs in at beoncarrier.gobeon.com and the extension hands the session over. A credential-REQUIRED board
     * still ignores this: there the extension path cannot be refreshed on expiry, so offering it alone would
     * promise a connection that is guaranteed to die.
     */
    extensionOnly?: boolean
}

interface LoadboardConfig {
    title: string
    url: string
    description: string
    buttonLabel: string
    waitingText: string
    successText: string
}

const LOADBOARD_CONFIGS: Record<AihubSource, LoadboardConfig> = {
    'truckstop-account': {
        title: 'TruckStop Account',
        url: 'https://main.truckstop.com/app/search/loads',
        description: 'To search TruckStop Account loads, you need to be logged in to your TruckStop account.',
        buttonLabel: 'Log in to TruckStop',
        waitingText: 'Waiting for you to log in at main.truckstop.com...',
        successText: 'Successfully connected to TruckStop Account!',
    },
    tql: {
        title: 'TQL Carrier Dashboard',
        url: 'https://carrierdashboard.tql.com/SearchLoads',
        description: 'To search TQL loads, you need to be logged in to your TQL Carrier Dashboard account.',
        buttonLabel: 'Log in to TQL Carrier Dashboard',
        waitingText: 'Waiting for you to log in at carrierdashboard.tql.com...',
        successText: 'Successfully connected to TQL!',
    },
    beon: {
        title: 'BEON (BeOnCarrier)',
        url: 'https://beoncarrier.gobeon.com',
        description: 'To search BEON loads, you need to be logged in to your BeOnCarrier account.',
        buttonLabel: 'Log in to BeOnCarrier',
        waitingText: 'Waiting for you to log in at beoncarrier.gobeon.com...',
        successText: 'Successfully connected to BEON!',
    },
    navisphere: {
        title: 'C.H. Robinson (Navisphere)',
        url: 'https://www.navispherecarrier.com',
        description: 'To search C.H. Robinson loads, you need to be logged in to your Navisphere Carrier account.',
        buttonLabel: 'Log in to Navisphere Carrier',
        waitingText: 'Waiting for you to log in at navispherecarrier.com...',
        successText: 'Successfully connected to C.H. Robinson!',
    },
    arrive: {
        title: 'Arrive Logistics',
        url: 'https://carrier.arrivelogistics.com',
        description: 'To search Arrive Logistics loads, you need to be logged in to your Arrive Carrier account.',
        buttonLabel: 'Log in to Arrive Logistics',
        waitingText: 'Waiting for you to log in at carrier.arrivelogistics.com...',
        successText: 'Successfully connected to Arrive Logistics!',
    },
    emerge: {
        title: 'Emerge Loadboard',
        url: 'https://app.emergemarket.io/cp',
        description: 'To search Emerge loads, you need to be logged in to your Emerge account.',
        buttonLabel: 'Log in to Emerge',
        waitingText: 'Waiting for you to log in at app.emergemarket.io...',
        successText: 'Successfully connected to Emerge!',
    },
    echo: {
        title: 'Echo (EchoDrive)',
        url: 'https://echodrive.echo.com/carrier/',
        description: 'To search Echo loads, you need to be logged in to your EchoDrive account.',
        buttonLabel: 'Log in to EchoDrive',
        waitingText: 'Waiting for you to log in at echodrive.echo.com...',
        successText: 'Successfully connected to Echo!',
    },
    spot: {
        title: 'SPOT (My Spot Carrier)',
        url: 'https://myspotcarrier.com/available-freight',
        description: 'To search SPOT loads, you need to be logged in to your My Spot Carrier account.',
        buttonLabel: 'Log in to My Spot Carrier',
        waitingText: 'Waiting for you to log in at myspotcarrier.com...',
        successText: 'Successfully connected to SPOT!',
    },
    rxo: {
        title: 'RXO Connect',
        url: 'https://carrier.rxoconnect.rxo.com/loads/available-loads',
        description: 'To search RXO loads, you need to be logged in to your RXO Connect carrier account.',
        buttonLabel: 'Log in to RXO Connect',
        waitingText: 'Waiting for you to log in at carrier.rxoconnect.rxo.com...',
        successText: 'Successfully connected to RXO!',
    },
    convoy: {
        title: 'Convoy Carrier',
        url: 'https://carrier.convoy.com/',
        description: 'To search Convoy loads, you need to be logged in to your Convoy Carrier account.',
        buttonLabel: 'Log in to Convoy',
        waitingText: 'Waiting for you to log in at carrier.convoy.com...',
        successText: 'Successfully connected to Convoy!',
    },
    'mode-global': {
        title: 'Mode Global',
        url: 'https://marketplace.modeglobal.com/#/loads/offers',
        description: 'To search Mode Global loads, you need to be logged in to your Mode Global account.',
        buttonLabel: 'Log in to Mode Global',
        waitingText: 'Waiting for you to log in at marketplace.modeglobal.com...',
        successText: 'Successfully connected to Mode Global!',
    },
    'jb-hunt': {
        title: 'J.B. Hunt 360',
        url: 'https://scm.jbhunt.com/carrier/ngx/load-board/map',
        description: 'To search J.B. Hunt loads, you need to be logged in to your JB Hunt 360 Carrier account.',
        buttonLabel: 'Log in to JB Hunt 360',
        waitingText: 'Waiting for you to log in at scm.jbhunt.com...',
        successText: 'Successfully connected to J.B. Hunt!',
    },
    knx: {
        title: 'KNX',
        url: 'https://knxloadboard.com/loadboard',
        description: 'To search KNX loads, you need to be logged in to your KNX account.',
        buttonLabel: 'Log in to KNX',
        waitingText: 'Waiting for you to log in at knxloadboard.com...',
        successText: 'Successfully connected to KNX!',
    },
    schneider: {
        title: 'Schneider FreightPower',
        url: 'https://freightpower.schneider.com/carrier/app/search',
        description: 'To search Schneider loads, you need to be logged in to your FreightPower carrier account.',
        buttonLabel: 'Log in to Schneider FreightPower',
        waitingText: 'Waiting for you to log in at freightpower.schneider.com...',
        successText: 'Successfully connected to Schneider!',
    },
    dat: {
        title: 'DAT One',
        url: 'https://one.dat.com/search-loads',
        description: 'To search DAT One loads, you need to be logged in to your DAT One account.',
        buttonLabel: 'Log in to DAT One',
        waitingText: 'Waiting for you to log in at one.dat.com...',
        successText: 'Successfully connected to DAT One!',
    },
    'uber-freight': {
        title: 'Uber Freight',
        url: 'https://www.uber.com/freight/carriers/fleet/search-loads',
        description: 'To search Uber Freight loads, you need to be logged in to your Uber Freight carrier account.',
        buttonLabel: 'Log in to Uber Freight',
        waitingText: 'Waiting for you to log in at uber.com/freight...',
        successText: 'Successfully connected to Uber Freight!',
    },
}

const CHROME_EXTENSION_URL = 'https://chromewebstore.google.com/detail/numeo-spot/mfhpdapldnbmejcanhlbbmjlojncicfj'

/** Poll cadence — 1.5s between status checks. Fast enough for sub-2s success path. */
const POLL_INTERVAL_MS = 1500
/** Grace period after the user closes the loadboard tab — enough for token handshake to land. */
const GRACE_AFTER_TAB_CLOSE_S = 10
/** Hard safety cap so a truly stuck session doesn't wait forever (3 minutes). */
const HARD_TIMEOUT_S = 180
/**
 * Interval between LOADBOARD_CONNECT messages while the loadboard tab is open.
 * Kept intentionally long (30s) — we only send when the tab is on the loadboard's
 * own domain (not an auth redirect page) to prevent background services from
 * creating duplicate tabs via ensureReadyTab()/createTab().
 */
const CONNECT_RETRY_INTERVAL_MS = 30_000

type Step = 'prompt' | 'creds' | 'otp' | 'waiting' | 'success' | 'error'

/** Loadboards that support credential-based login (in addition to the extension flow) */
// const CREDENTIAL_LOGIN_BOARDS = new Set<AihubSource>([
//     'tql',
//     'navisphere',
//     'beon',
//     'emerge',
//     'echo',
//     'mode-global',
//     'knx',
//     'convoy',
//     'arrive',
//     'spot',
//     'rxo',
//     'schneider',
// ])
const CREDENTIAL_LOGIN_BOARDS = new Set<AihubSource>([
    'tql',
    'navisphere',
    'beon',
    'emerge',
    'echo',
    'mode-global',
    'knx',
    'convoy',
    'arrive',
    'spot',
    'rxo',
    'schneider',
    'truckstop-account',
])
/** Loadboards that use phone number (instead of email+password) for credential login */
const PHONE_LOGIN_BOARDS = new Set<AihubSource>(['convoy', 'spot'])

/**
 * Boards whose session CANNOT be sustained through the extension: no OTP, and the backend keeps
 * the connection alive by silently re-logging in with the stored email+password when the token
 * expires. An extension-captured token has no password, so such a connection would silently die on
 * expiry with no way to auto-reconnect. For these boards, credential login is the ONLY reliable
 * path — always offered (even if the loadHubNewAuth flag is off) and the extension option is hidden.
 */
const CREDENTIAL_REQUIRED_BOARDS = new Set<AihubSource>(['knx'])

/**
 * Credential inputs. The background and text color are declared EXPLICITLY (with dark
 * counterparts) rather than left to inherit: Tailwind v4's preflight sets
 * `background-color: transparent` + `color: inherit` on form controls, so an unstyled
 * input adopts whatever the surrounding MUI Dialog paper computes — which rendered the
 * typed email/password near-black on the dark dialog surface. `gray-*` in particular can
 * never be left unpaired here: `styles/index.css` remaps only the SLATE scale under
 * `.dark`, so a bare `text-gray-900` stays #111827 in dark mode.
 */
const inputClass =
    'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500 dark:disabled:bg-slate-900 dark:disabled:text-slate-500'

export const AihubAuthModal: React.FC<AihubAuthModalProps> = ({
    open,
    loadboard,
    onClose,
    onSuccess,
    checkStatus,
    onStatusRefreshed,
    initialStatus,
    analyticsSurface,
    extensionOnly,
}) => {
    const featureFlags = useAppSelector(selectFeatureFlags)
    const credentialLoginEnabled = featureFlags.loadHubNewAuth ?? false
    // KNX (and other credential-required boards) must use email+password so the backend can
    // auto-relogin on expiry — always offer it, even if the new-auth flag is off, and hide the
    // extension path (an extension token can't be auto-refreshed → guaranteed disconnect).
    const credentialRequired = !!loadboard && CREDENTIAL_REQUIRED_BOARDS.has(loadboard)
    const showCredentialLogin =
        !!loadboard && CREDENTIAL_LOGIN_BOARDS.has(loadboard) && (credentialLoginEnabled || credentialRequired) && (!extensionOnly || credentialRequired)

    const [step, setStep] = useState<Step>('prompt')
    const [elapsedSeconds, setElapsedSeconds] = useState(0)
    const [tabClosed, setTabClosed] = useState(false)
    const [graceSecondsLeft, setGraceSecondsLeft] = useState(GRACE_AFTER_TAB_CLOSE_S)
    const openedTabRef = useRef<Window | null>(null)
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const tickIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const connectRetryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const loadboardRef = useRef<AihubSource | null>(null)
    const attemptRef = useRef<ConnectionAttempt | null>(null)
    const previousAttemptIdRef = useRef<string | undefined>(undefined)
    const connectionFlowIdRef = useRef<string | undefined>(undefined)
    const flowGenerationRef = useRef(0)
    const previousOpenRef = useRef(false)
    const previousLoadboardRef = useRef(loadboard)
    const initialStatusRef = useRef(initialStatus)
    initialStatusRef.current = initialStatus
    const analyticsEnabled = analyticsSurface === 'load_hub'
    const methodsOffered = useMemo(() => {
        if (!loadboard) return []
        const credentialMethod = PHONE_LOGIN_BOARDS.has(loadboard) ? 'phone_otp' : 'email_password'
        if (credentialRequired) return [credentialMethod]
        if (showCredentialLogin) return [credentialMethod, 'extension_session_capture']
        return ['extension_session_capture']
    }, [credentialRequired, loadboard, showCredentialLogin])

    const completeSuccessfulAttempt = useCallback(
        (board: AihubSource): void => {
            const attempt = attemptRef.current
            attemptRef.current = null
            if (!attempt) return
            completeSuccessfulConnectionAttempt(board, attempt, () => {
                void checkStatus(board)
                    .then((status) => onStatusRefreshed?.(board, status))
                    .catch(() => undefined)
            })
        },
        [checkStatus, onStatusRefreshed]
    )

    // Credential-based login state (shared for TQL, Navisphere, etc.)
    const [credLogin, setCredLogin] = useState('')
    const [credPassword, setCredPassword] = useState('')
    const [credOtpCode, setCredOtpCode] = useState('')
    const [credSessionId, setCredSessionId] = useState<string | null>(null)
    const [credLoading, setCredLoading] = useState(false)
    const [credError, setCredError] = useState<string | null>(null)
    // Arrive-only: factor Okta auto-selected (SMS vs authenticator app). Captured from the
    // login response and used to render the right hint in the OTP step.
    const [arriveMethod, setArriveMethod] = useState<ArriveOtpMethod | null>(null)

    // Track which loadboard we're authenticating (stable across re-renders)
    useEffect(() => {
        if (loadboard) loadboardRef.current = loadboard
    }, [loadboard])

    const config = loadboard ? LOADBOARD_CONFIGS[loadboard] : null

    /** Tear down timers and ensure the opened loadboard tab (if any) is closed. */
    const cleanup = useCallback((closeOpenedTab: boolean) => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current)
            pollIntervalRef.current = null
        }
        if (tickIntervalRef.current) {
            clearInterval(tickIntervalRef.current)
            tickIntervalRef.current = null
        }
        if (connectRetryIntervalRef.current) {
            clearInterval(connectRetryIntervalRef.current)
            connectRetryIntervalRef.current = null
        }
        if (closeOpenedTab && openedTabRef.current) {
            try {
                openedTabRef.current.close()
            } catch {
                // Tab may already be closed or cross-origin — ignore
            }
            openedTabRef.current = null
        }
    }, [])

    const resetCredState = useCallback(() => {
        setCredLogin('')
        setCredPassword('')
        setCredOtpCode('')
        setCredSessionId(null)
        setCredLoading(false)
        setCredError(null)
        setArriveMethod(null)
    }, [])

    const handleClose = useCallback(() => {
        flowGenerationRef.current += 1
        attemptRef.current?.complete('cancelled')
        attemptRef.current = null
        cleanup(true)
        setStep('prompt')
        setElapsedSeconds(0)
        setTabClosed(false)
        setGraceSecondsLeft(GRACE_AFTER_TAB_CLOSE_S)
        resetCredState()
        onClose()
    }, [cleanup, onClose, resetCredState])

    const handleCredentialsSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault()
            if (!loadboard) return
            const generation = flowGenerationRef.current
            attemptRef.current = beginConnectionAttempt({
                board: loadboard,
                authFlow: PHONE_LOGIN_BOARDS.has(loadboard) ? 'otp' : 'credentials',
                hasEverConnected: initialStatus?.hasEverConnected,
                connectionSequence: initialStatus?.connectionSequence,
                analyticsEnabled,
                connectionFlowId: connectionFlowIdRef.current,
                methodsOffered,
                previousAttemptId: previousAttemptIdRef.current,
            })
            previousAttemptIdRef.current = attemptRef.current.attemptId
            attemptRef.current.recordStep('credentials_submit', 'submitted')
            setCredLoading(true)
            setCredError(null)
            try {
                await attemptRef.current.registrationPromise
                if (flowGenerationRef.current !== generation) return
                let requiresOtp = false
                let sessionId: string | undefined
                let observedOtpMethod: 'sms' | 'authenticator_app' | 'unknown' = PHONE_LOGIN_BOARDS.has(loadboard) ? 'sms' : 'unknown'
                let nextArriveMethod: 'sms' | 'totp' | undefined

                if (loadboard === 'tql') {
                    const result = await tqlLoginCredentials(credLogin, credPassword)
                    requiresOtp = result.requiresOtp
                    sessionId = result.sessionId
                } else if (loadboard === 'navisphere') {
                    const result = await navisphereLoginCredentials(credLogin, credPassword)
                    requiresOtp = result.requiresOtp
                    sessionId = result.sessionId
                } else if (loadboard === 'beon') {
                    const result = await beonLoginCredentials(credLogin, credPassword)
                    requiresOtp = result.requiresOtp
                    sessionId = result.sessionId
                } else if (loadboard === 'emerge') {
                    await emergeLoginCredentials(credLogin, credPassword)
                } else if (loadboard === 'echo') {
                    const result = await echoLoginCredentials(credLogin, credPassword)
                    requiresOtp = result.requiresOtp
                    sessionId = result.sessionId
                } else if (loadboard === 'mode-global') {
                    await modeGlobalLoginCredentials(credLogin, credPassword)
                } else if (loadboard === 'knx') {
                    await knxLoginCredentials(credLogin, credPassword)
                } else if (loadboard === 'convoy') {
                    const result = await convoyLoginCredentials(credLogin)
                    requiresOtp = result.requiresOtp
                    sessionId = result.sessionId
                } else if (loadboard === 'spot') {
                    const result = await spotLoginCredentials(credLogin)
                    requiresOtp = result.requiresOtp
                    sessionId = result.sessionId
                } else if (loadboard === 'arrive') {
                    const result = await arriveLoginCredentials(credLogin, credPassword)
                    requiresOtp = result.requiresOtp
                    sessionId = result.sessionId
                    nextArriveMethod = result.method
                    observedOtpMethod = result.method === 'totp' ? 'authenticator_app' : 'sms'
                } else if (loadboard === 'rxo') {
                    const result = await rxoLoginCredentials(credLogin, credPassword)
                    requiresOtp = result.requiresOtp
                    sessionId = result.sessionId
                } else if (loadboard === 'schneider') {
                    const result = await schneiderLoginCredentials(credLogin, credPassword)
                    requiresOtp = result.requiresOtp
                    sessionId = result.sessionId
                } else if (loadboard === 'truckstop-account') {
                    const result = await truckstopAccountLoginCredentials(credLogin, credPassword)
                    requiresOtp = result.requiresOtp
                    sessionId = result.sessionId
                }

                if (flowGenerationRef.current !== generation) return
                if (nextArriveMethod) setArriveMethod(nextArriveMethod)

                if (requiresOtp && sessionId) {
                    attemptRef.current?.recordStep('credentials_submit', 'succeeded')
                    attemptRef.current?.recordStep('otp_challenge', 'succeeded', {
                        otpMethod: observedOtpMethod,
                    })
                    attemptRef.current?.recordStep('otp_verify', 'entered', {
                        otpMethod: observedOtpMethod,
                    })
                    setCredSessionId(sessionId)
                    setStep('otp')
                } else {
                    attemptRef.current?.recordStep('credentials_submit', 'succeeded')
                    attemptRef.current?.recordStep('session_persist', 'succeeded')
                    completeSuccessfulAttempt(loadboard)
                    setStep('success')
                    setTimeout(() => {
                        if (flowGenerationRef.current !== generation) return
                        setStep('prompt')
                        resetCredState()
                        onSuccess()
                    }, 1500)
                }
            } catch (err: unknown) {
                if (flowGenerationRef.current !== generation) return
                attemptRef.current?.recordStep('credentials_submit', 'failed', { error: err })
                attemptRef.current?.complete('failure', { error: err })
                attemptRef.current = null
                const message =
                    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                    (err as Error).message ||
                    'Login failed. Please check your credentials.'
                setCredError(message)
            } finally {
                if (flowGenerationRef.current === generation) setCredLoading(false)
            }
        },
        [
            loadboard,
            credLogin,
            credPassword,
            initialStatus?.hasEverConnected,
            initialStatus?.connectionSequence,
            onSuccess,
            resetCredState,
            completeSuccessfulAttempt,
            analyticsEnabled,
            methodsOffered,
        ]
    )

    const handleOtpSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault()
            if (!credSessionId) return
            const generation = flowGenerationRef.current
            if (!attemptRef.current && loadboard) {
                attemptRef.current = beginConnectionAttempt({
                    board: loadboard,
                    authFlow: 'otp',
                    hasEverConnected: initialStatus?.hasEverConnected,
                    connectionSequence: initialStatus?.connectionSequence,
                    analyticsEnabled,
                    connectionFlowId: connectionFlowIdRef.current,
                    methodsOffered,
                    previousAttemptId: previousAttemptIdRef.current,
                })
                previousAttemptIdRef.current = attemptRef.current.attemptId
            }
            const otpMethod =
                loadboard === 'arrive'
                    ? arriveMethod === 'totp'
                        ? 'authenticator_app'
                        : arriveMethod === 'sms'
                          ? 'sms'
                          : 'unknown'
                    : loadboard && PHONE_LOGIN_BOARDS.has(loadboard)
                      ? 'sms'
                      : 'unknown'
            attemptRef.current?.recordStep('otp_verify', 'submitted', { otpMethod })
            setCredLoading(true)
            setCredError(null)
            try {
                await attemptRef.current?.registrationPromise
                if (flowGenerationRef.current !== generation) return
                if (loadboard === 'tql') {
                    await tqlVerifyOtp(credSessionId, credOtpCode)
                } else if (loadboard === 'navisphere') {
                    await navisphereVerifyOtp(credSessionId, credOtpCode)
                } else if (loadboard === 'beon') {
                    await beonVerifyOtp(credSessionId, credOtpCode)
                } else if (loadboard === 'echo') {
                    await echoVerifyOtp(credSessionId, credOtpCode)
                } else if (loadboard === 'convoy') {
                    await convoyVerifyOtp(credSessionId, credOtpCode)
                } else if (loadboard === 'spot') {
                    await spotVerifyOtp(credSessionId, credOtpCode)
                } else if (loadboard === 'arrive') {
                    await arriveVerifyOtp(credSessionId, credOtpCode)
                } else if (loadboard === 'rxo') {
                    await rxoVerifyOtp(credSessionId, credOtpCode)
                } else if (loadboard === 'schneider') {
                    await schneiderVerifyOtp(credSessionId, credOtpCode)
                } else if (loadboard === 'truckstop-account') {
                    await truckstopAccountVerifyOtp(credSessionId, credOtpCode)
                }
                if (flowGenerationRef.current !== generation) return
                attemptRef.current?.recordStep('otp_verify', 'succeeded', { otpMethod })
                attemptRef.current?.recordStep('session_persist', 'succeeded')
                if (loadboard) completeSuccessfulAttempt(loadboard)
                setStep('success')
                setTimeout(() => {
                    if (flowGenerationRef.current !== generation) return
                    setStep('prompt')
                    resetCredState()
                    onSuccess()
                }, 1500)
            } catch (err: unknown) {
                if (flowGenerationRef.current !== generation) return
                attemptRef.current?.recordStep('otp_verify', 'failed', { error: err, otpMethod })
                attemptRef.current?.complete('failure', { error: err })
                attemptRef.current = null
                const message =
                    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                    (err as Error).message ||
                    'Invalid code. Please try again.'
                setCredError(message)
            } finally {
                if (flowGenerationRef.current === generation) setCredLoading(false)
            }
        },
        [
            loadboard,
            credSessionId,
            credOtpCode,
            initialStatus?.hasEverConnected,
            initialStatus?.connectionSequence,
            onSuccess,
            resetCredState,
            completeSuccessfulAttempt,
            analyticsEnabled,
            methodsOffered,
            arriveMethod,
        ]
    )

    /**
     * Begin the login handshake: open loadboard URL, start tab monitoring + status poll.
     * Used by both the initial "Log in" click and the Retry button in the error step.
     */
    const startHandshake = useCallback(() => {
        if (!config) return
        const currentBoard = loadboardRef.current
        if (!currentBoard) return
        const generation = flowGenerationRef.current

        // Always close any stale opened tab before opening a fresh one — stops the "two tabs"
        // problem when the user hits Retry after a previous attempt left a stuck tab open.
        cleanup(true)

        openedTabRef.current = window.open(config.url, '_blank')
        if (analyticsEnabled && connectionFlowIdRef.current) {
            trackConnectionMethodSelected({
                board: currentBoard,
                connectionFlowId: connectionFlowIdRef.current,
                connectionMethod: 'extension_session_capture',
                methodsOffered,
                selectionOrigin: methodsOffered.length === 1 ? 'only_available_method' : 'user',
            })
        }
        attemptRef.current = beginConnectionAttempt({
            board: currentBoard,
            authFlow: 'browser_extension',
            hasEverConnected: initialStatus?.hasEverConnected,
            connectionSequence: initialStatus?.connectionSequence,
            analyticsEnabled,
            connectionFlowId: connectionFlowIdRef.current,
            methodsOffered,
            previousAttemptId: previousAttemptIdRef.current,
        })
        previousAttemptIdRef.current = attemptRef.current.attemptId
        attemptRef.current.recordStep('provider_tab_open', openedTabRef.current ? 'succeeded' : 'failed')
        if (openedTabRef.current) attemptRef.current.recordStep('provider_login_wait', 'entered')
        setStep('waiting')
        setElapsedSeconds(0)
        setTabClosed(false)
        setGraceSecondsLeft(GRACE_AFTER_TAB_CLOSE_S)

        const startedAt = Date.now()
        let tabClosedAt: number | null = null
        let isDone = false

        const finish = (nextStep: Step, failureReason?: string) => {
            if (isDone || flowGenerationRef.current !== generation) return
            isDone = true
            // The extension-handshake path (window.open + status poll) is how most boards connect —
            // several have no credential dialog at all — so tracking only the credential/OTP
            // branches would miss them entirely. `isDone` above makes this fire at most once.
            if (nextStep === 'success') {
                attemptRef.current?.recordStep('session_transfer', 'succeeded', { owner: 'extension' })
                completeSuccessfulAttempt(currentBoard)
            } else {
                attemptRef.current?.recordStep('connection_verify', 'failed')
                attemptRef.current?.complete('failure', { failureReason })
            }
            attemptRef.current = null
            connectTimeouts.forEach(clearTimeout)
            cleanup(nextStep === 'success')
            if (nextStep === 'error') {
                // Don't close the tab on error — user might still be on it
                openedTabRef.current = null
            }
            setStep(nextStep)
            if (nextStep === 'success') {
                setTimeout(() => {
                    if (flowGenerationRef.current !== generation) return
                    setStep('prompt')
                    onSuccess()
                }, 1500)
            }
        }

        // Tell extension to actively extract token from the loadboard tab.
        // Note: we can't read cross-origin tab URLs (SecurityError), so we simply check
        // that the tab is still open and fire unconditionally. The background service's
        // ensureReadyTab() will ping the content script and handle the case where the
        // user hasn't finished logging in yet (ping will fail gracefully; next retry wins).
        let connectSent = false
        const triggerExtensionConnect = () => {
            if (!currentBoard || isDone || connectSent || flowGenerationRef.current !== generation) return
            connectSent = true
            attemptRef.current?.recordStep('session_capture', 'submitted', { owner: 'extension' })
            window.postMessage({ source: 'numeo_private_loads', type: 'LOADBOARD_CONNECT', loadboard: currentBoard }, '*')
            // Allow another attempt after cooldown
            setTimeout(() => {
                connectSent = false
            }, 15000)
        }

        // Single initial attempt after 5s (for already-logged-in users).
        const connectTimeouts = [
            setTimeout(() => {
                if (!openedTabRef.current || openedTabRef.current.closed) return
                triggerExtensionConnect()
            }, 5000),
        ]

        // Periodic retry — fires every 30s while the tab is open so users who need
        // to actually log in get a fresh attempt once they land back on the loadboard.
        connectRetryIntervalRef.current = setInterval(() => {
            if (isDone) return
            if (!openedTabRef.current || openedTabRef.current.closed) return
            triggerExtensionConnect()
        }, CONNECT_RETRY_INTERVAL_MS)

        // Tick every 1s — monitors tab state, elapsed time, grace period.
        tickIntervalRef.current = setInterval(() => {
            if (isDone || flowGenerationRef.current !== generation) return

            const elapsed = Math.floor((Date.now() - startedAt) / 1000)
            setElapsedSeconds(elapsed)

            // Hard safety cap
            if (elapsed >= HARD_TIMEOUT_S) {
                finish('error', 'timeout')
                return
            }

            // Check if the opened tab has been closed by the user
            const isTabClosed = !openedTabRef.current || openedTabRef.current.closed
            if (isTabClosed && !tabClosedAt) {
                tabClosedAt = Date.now()
                setTabClosed(true)
                setGraceSecondsLeft(GRACE_AFTER_TAB_CLOSE_S)
            }

            // Grace period countdown after tab close
            if (tabClosedAt) {
                const graceElapsed = Math.floor((Date.now() - tabClosedAt) / 1000)
                const remaining = Math.max(0, GRACE_AFTER_TAB_CLOSE_S - graceElapsed)
                setGraceSecondsLeft(remaining)
                if (remaining <= 0) {
                    finish('error', 'popup_closed')
                }
            }
        }, 1000)

        // Status poll — 1.5s cadence, stops on success or timeout.
        pollIntervalRef.current = setInterval(async () => {
            try {
                if (!currentBoard || isDone) return
                const boardStatus = await checkStatus(currentBoard)
                if (flowGenerationRef.current !== generation) return
                if (boardStatus?.isConnected) {
                    finish('success')
                }
            } catch {
                // Individual poll failures are benign — just try again next tick.
            }
        }, POLL_INTERVAL_MS)
    }, [
        analyticsEnabled,
        checkStatus,
        cleanup,
        completeSuccessfulAttempt,
        config,
        initialStatus?.connectionSequence,
        initialStatus?.hasEverConnected,
        methodsOffered,
        onSuccess,
    ])

    /** Component unmount safety — close the analytics attempt and never leave a rogue interval running. */
    useEffect(
        () => () => {
            flowGenerationRef.current += 1
            attemptRef.current?.complete('cancelled')
            attemptRef.current = null
            cleanup(false)
        },
        [cleanup]
    )

    useEffect(() => {
        if (!open) {
            flowGenerationRef.current += 1
            if (attemptRef.current) {
                attemptRef.current.complete('cancelled')
                attemptRef.current = null
            }
        }
    }, [open])

    useEffect(() => {
        const wasOpen = previousOpenRef.current
        const previousLoadboard = previousLoadboardRef.current
        previousOpenRef.current = open
        previousLoadboardRef.current = loadboard
        const boardChangedWhileOpen = open && wasOpen && previousLoadboard !== loadboard
        if (open && (!wasOpen || boardChangedWhileOpen)) {
            flowGenerationRef.current += 1
            if (boardChangedWhileOpen) {
                attemptRef.current?.complete('cancelled')
                attemptRef.current = null
                cleanup(true)
            }
            const statusAtOpen = initialStatusRef.current
            attemptRef.current = null
            setStep('prompt')
            setElapsedSeconds(0)
            setTabClosed(false)
            setGraceSecondsLeft(GRACE_AFTER_TAB_CLOSE_S)
            resetCredState()
            previousAttemptIdRef.current = undefined
            connectionFlowIdRef.current =
                analyticsEnabled && loadboard
                    ? trackConnectionDialogOpened({
                          board: loadboard,
                          methodsOffered,
                          newAuthEnabled: credentialLoginEnabled,
                          priorConnectionState: statusAtOpen?.isConnected ? 'connected' : statusAtOpen?.hasEverConnected ? 'disconnected' : 'unknown',
                      })
                    : undefined
        }
    }, [analyticsEnabled, cleanup, credentialLoginEnabled, loadboard, methodsOffered, open, resetCredState])

    if (!config) return null

    const formatTime = (s: number) => {
        const m = Math.floor(s / 60)
        const sec = s % 60
        return m > 0 ? `${m}m ${sec}s` : `${sec}s`
    }

    return (
        <Dialog open={open} onClose={step === 'waiting' ? undefined : handleClose} maxWidth="xs" fullWidth PaperProps={{ className: 'dark:bg-slate-900' }}>
            <DialogTitle className="flex items-center justify-between pr-2">
                <span className="text-base font-semibold text-gray-900 dark:text-slate-100">{config.title}</span>
                {step !== 'success' && (
                    <IconButton onClick={handleClose} size="sm" variant="plain" color="neutral">
                        <Close fontSize="small" />
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent>
                {step === 'prompt' && (
                    <div className="pt-1 pb-2">
                        {/* Keyed on what this modal OFFERS, not on the board. A board that CAN take a password
                            still needs this banner wherever the password is not on the table — `extensionOnly`
                            on the Box Truck board, and equally Load Hub with `loadHubNewAuth` off. Keyed on the
                            board instead, such an operator was sent to log in and then watched the modal wait
                            for a session the extension was never there to capture, until the 180s timeout. */}
                        {!showCredentialLogin && localStorage.getItem(HAS_EXTENSION_KEY) !== 'true' && (
                            <div className="flex items-start gap-2.5 p-3 mb-4 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-700/40">
                                <ExtensionIcon sx={{ fontSize: 18, color: '#d97706', flexShrink: 0, marginTop: '1px' }} />
                                <div>
                                    <p className="text-[13px] font-medium text-amber-800 mb-1 dark:text-amber-300">Numeo Extension required</p>
                                    <p className="text-[12px] text-amber-700 leading-relaxed dark:text-amber-400">
                                        This loadboard requires the Numeo Chrome Extension to capture your session.{' '}
                                        <a
                                            href={CHROME_EXTENSION_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-medium text-blue-600 hover:text-blue-700 underline dark:text-blue-400 dark:hover:text-blue-300"
                                        >
                                            Install Extension
                                        </a>
                                    </p>
                                </div>
                            </div>
                        )}
                        <p className="text-sm text-gray-500 mb-4 dark:text-slate-400">{config.description}</p>
                        {showCredentialLogin ? (
                            <div className="flex flex-col gap-2">
                                {!credentialRequired && <p className="text-sm text-gray-500 mb-1 dark:text-slate-400">Choose how you want to connect:</p>}
                                <button
                                    onClick={() => {
                                        if (analyticsEnabled && loadboard && connectionFlowIdRef.current) {
                                            trackConnectionMethodSelected({
                                                board: loadboard,
                                                connectionFlowId: connectionFlowIdRef.current,
                                                connectionMethod: PHONE_LOGIN_BOARDS.has(loadboard) ? 'phone_otp' : 'email_password',
                                                methodsOffered,
                                                selectionOrigin: methodsOffered.length === 1 ? 'only_available_method' : 'user',
                                            })
                                        }
                                        setStep('creds')
                                    }}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-md py-2.5 flex items-center justify-center gap-2 transition-colors"
                                >
                                    <Lock sx={{ fontSize: 18 }} />
                                    {loadboard && PHONE_LOGIN_BOARDS.has(loadboard) ? 'Connect with Phone & OTP' : 'Connect with Email & Password'}
                                </button>
                                {!credentialRequired && (
                                    <button
                                        onClick={startHandshake}
                                        className="w-full bg-transparent hover:bg-gray-100 text-gray-700 font-medium text-sm rounded-md py-2.5 border border-gray-200 flex items-center justify-center gap-2 transition-colors dark:text-slate-300 dark:border-slate-700 dark:hover:bg-slate-800"
                                    >
                                        <ExtensionIcon sx={{ fontSize: 18 }} />
                                        Connect with Extension
                                    </button>
                                )}
                            </div>
                        ) : (
                            <>
                                <p className="text-sm text-gray-500 mb-5 dark:text-slate-400">
                                    Click the button below to open it in a new tab and log in. Your session will be detected automatically.
                                </p>
                                <button
                                    onClick={startHandshake}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-md py-2.5 flex items-center justify-center gap-2 transition-colors"
                                >
                                    <OpenInNew sx={{ fontSize: 18 }} />
                                    {config.buttonLabel}
                                </button>
                            </>
                        )}
                    </div>
                )}

                {step === 'creds' && (
                    <div className="pt-1 pb-2">
                        <button
                            onClick={() => setStep('prompt')}
                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-4 transition-colors dark:text-slate-400 dark:hover:text-slate-200"
                        >
                            <ArrowBack sx={{ fontSize: 15 }} />
                            Back
                        </button>
                        <form onSubmit={handleCredentialsSubmit} className="flex flex-col gap-3">
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                                    {loadboard && PHONE_LOGIN_BOARDS.has(loadboard)
                                        ? 'Phone Number'
                                        : loadboard === 'navisphere'
                                          ? 'Username or Email'
                                          : 'Email'}
                                </label>
                                <input
                                    type={loadboard && PHONE_LOGIN_BOARDS.has(loadboard) ? 'tel' : loadboard === 'navisphere' ? 'text' : 'email'}
                                    value={credLogin}
                                    onChange={(e) => {
                                        if (loadboard && PHONE_LOGIN_BOARDS.has(loadboard)) {
                                            setCredLogin(formatPhone(e.target.value))
                                        } else {
                                            setCredLogin(e.target.value)
                                        }
                                    }}
                                    placeholder={
                                        loadboard && PHONE_LOGIN_BOARDS.has(loadboard)
                                            ? '+1 (___) ___-____'
                                            : loadboard === 'navisphere'
                                              ? 'your@email.com'
                                              : 'you@example.com'
                                    }
                                    required
                                    autoFocus
                                    disabled={credLoading}
                                    className={inputClass}
                                />
                            </div>
                            {loadboard && !PHONE_LOGIN_BOARDS.has(loadboard) && (
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-gray-700 dark:text-slate-300">Password</label>
                                    <PasswordInput
                                        value={credPassword}
                                        onChange={(e) => setCredPassword(e.target.value)}
                                        placeholder="••••••••"
                                        required
                                        disabled={credLoading}
                                        className={inputClass}
                                    />
                                </div>
                            )}
                            {credError && <p className="text-xs text-red-600 dark:text-red-400">{credError}</p>}
                            <button
                                type="submit"
                                disabled={credLoading || !credLogin || (loadboard !== null && !PHONE_LOGIN_BOARDS.has(loadboard!) && !credPassword)}
                                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium text-sm rounded-md py-2.5 flex items-center justify-center gap-2 transition-colors mt-1"
                            >
                                {credLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Lock sx={{ fontSize: 18 }} />}
                                {credLoading ? 'Sending code…' : loadboard && PHONE_LOGIN_BOARDS.has(loadboard) ? 'Send Code' : 'Sign In'}
                            </button>
                        </form>
                    </div>
                )}

                {step === 'otp' && (
                    <div className="pt-1 pb-2">
                        <button
                            onClick={() => {
                                setStep('creds')
                                setCredError(null)
                                setCredOtpCode('')
                            }}
                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-4 transition-colors dark:text-slate-400 dark:hover:text-slate-200"
                        >
                            <ArrowBack sx={{ fontSize: 15 }} />
                            Back
                        </button>
                        <div className="flex flex-col gap-1 mb-4">
                            <p className="text-sm font-medium text-gray-800 dark:text-slate-200">Enter verification code</p>
                            <p className="text-xs text-gray-500 dark:text-slate-400">
                                {loadboard === 'navisphere'
                                    ? 'A one-time code was sent to your email. Enter it below to complete login.'
                                    : loadboard === 'arrive' && arriveMethod === 'totp'
                                      ? 'Open your authenticator app (Google Authenticator / Okta Verify) and enter the current code below.'
                                      : 'A one-time code was sent to your phone via SMS. Enter it below to complete login.'}
                            </p>
                        </div>
                        <form onSubmit={handleOtpSubmit} className="flex flex-col gap-3">
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-gray-700 dark:text-slate-300">Verification Code</label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={credOtpCode}
                                    onChange={(e) => setCredOtpCode(e.target.value.replace(/\D/g, ''))}
                                    placeholder="123456"
                                    maxLength={8}
                                    required
                                    autoFocus
                                    disabled={credLoading}
                                    className={inputClass}
                                />
                            </div>
                            {credError && <p className="text-xs text-red-600 dark:text-red-400">{credError}</p>}
                            <button
                                type="submit"
                                disabled={credLoading || !credOtpCode}
                                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium text-sm rounded-md py-2.5 flex items-center justify-center gap-2 transition-colors mt-1"
                            >
                                {credLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
                                {credLoading ? 'Verifying…' : 'Verify Code'}
                            </button>
                        </form>
                    </div>
                )}

                {step === 'waiting' && (
                    <div className="pt-1 pb-4 flex flex-col items-center gap-4">
                        <CircularProgress size={32} />
                        <p className="text-sm text-gray-600 text-center dark:text-slate-300">{config.waitingText}</p>
                        {tabClosed ? (
                            <div className="w-full flex flex-col gap-1.5">
                                <div className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-slate-700 overflow-hidden">
                                    <div
                                        className="h-full bg-amber-500 transition-[width] duration-1000 ease-linear"
                                        style={{ width: `${(graceSecondsLeft / GRACE_AFTER_TAB_CLOSE_S) * 100}%` }}
                                    />
                                </div>
                                <p className="text-xs text-amber-600 text-center tabular-nums dark:text-amber-400">
                                    Tab closed — finalizing connection… {graceSecondsLeft}s
                                </p>
                            </div>
                        ) : (
                            <div className="w-full flex flex-col gap-1.5">
                                <div className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-slate-700 overflow-hidden">
                                    <div className="h-full bg-blue-600 animate-pulse" style={{ width: '100%' }} />
                                </div>
                                <p className="text-xs text-gray-500 text-center tabular-nums dark:text-slate-400">
                                    Waiting for login… {formatTime(elapsedSeconds)}
                                </p>
                            </div>
                        )}
                        <p className="text-xs text-gray-400 text-center leading-relaxed dark:text-slate-500">
                            {tabClosed
                                ? 'Checking for your session before the window closes…'
                                : 'Take your time to log in — this window will update as soon as your session is detected.'}
                        </p>
                    </div>
                )}

                {step === 'success' && (
                    <div className="pt-1 pb-4 flex flex-col items-center gap-3">
                        <CheckCircle sx={{ fontSize: 48, color: '#16a34a' }} />
                        <p className="text-sm font-medium text-green-700 dark:text-green-400">{config.successText}</p>
                    </div>
                )}

                {step === 'error' && (
                    <div className="pt-1 pb-2 flex flex-col items-center gap-4">
                        <ErrorOutline sx={{ fontSize: 44, color: '#dc2626' }} />
                        <div className="flex flex-col gap-1.5 text-center">
                            <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">Couldn&apos;t connect to {config.title}</p>
                            <p className="text-[13px] text-gray-600 leading-relaxed dark:text-slate-400">
                                We didn&apos;t detect a session. Please make sure you&apos;re fully logged in to <strong>{config.title}</strong>, then try
                                again.
                            </p>
                        </div>
                        <div className="w-full flex flex-col gap-2">
                            <button
                                onClick={startHandshake}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-md py-2.5 flex items-center justify-center gap-2 transition-colors"
                            >
                                <Refresh sx={{ fontSize: 18 }} />
                                Retry
                            </button>
                            <button
                                onClick={handleClose}
                                className="w-full bg-transparent hover:bg-gray-100 text-gray-700 font-medium text-sm rounded-md py-2.5 border border-gray-200 transition-colors dark:text-slate-300 dark:border-slate-700 dark:hover:bg-slate-800"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
